/**
 * @file     landSpecs.ts
 * @purpose  Vacant land carries no dwelling specs. The DDF feed puts a land
 *           listing's lot area in LivingArea and fills beds and baths with 0,
 *           so a lot read as "0 beds, 47,916 sq ft interior, $6/sqft". This is
 *           the one rule that turns those fields back into a lot size.
 */

/** Vacant land / lot / acreage property types. */
export function isLotPropertyType(propertyType: string | null | undefined): boolean {
  return /\b(land|lot|acreage)\b/i.test(propertyType ?? '');
}

/**
 * Land by property type, or by a sub type that is exactly land. The sub type
 * check stays exact because BCRES ships "House with Acreage", which is a home.
 */
export function isVacantLand(
  propertyType: string | null | undefined,
  propertySubType: string | null | undefined,
): boolean {
  return isLotPropertyType(propertyType) || /^\s*(vacant\s+)?land\s*$/i.test(propertySubType ?? '');
}

interface LandFields {
  propertyType: string | null;
  propertySubType?: string | null;
  bedroomsTotal: number | null;
  bathroomsTotalInteger: number | null;
  bathroomsFull?: number | null;
  bathroomsHalf?: number | null;
  livingArea: number | null;
  lotSizeSquareFeet: number | null;
}

/**
 * For land: no beds, baths or interior area, and the LivingArea value becomes
 * the lot size when the feed sent no lot size of its own.
 */
export function withLandSpecs<T extends LandFields>(listing: T): T {
  if (!isVacantLand(listing.propertyType, listing.propertySubType)) return listing;
  const area = listing.livingArea != null && listing.livingArea > 0 ? listing.livingArea : null;
  return {
    ...listing,
    bedroomsTotal: null,
    bathroomsTotalInteger: null,
    ...('bathroomsFull' in listing ? { bathroomsFull: null } : null),
    ...('bathroomsHalf' in listing ? { bathroomsHalf: null } : null),
    livingArea: null,
    lotSizeSquareFeet: listing.lotSizeSquareFeet != null && listing.lotSizeSquareFeet > 0 ? listing.lotSizeSquareFeet : area,
  };
}
